import React, { useState, useEffect, useCallback } from "react";
import { Button, Modal, Form, Spinner, Alert } from "react-bootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import Swal from "sweetalert2";
import Select from "react-select";

import { formatMiles } from "../../../utils/miles";
import {
  getAllPagoProveedores,
  addPagoProveedor,
  editPagoProveedor,
  deletePagoProveedor,
  getAllProveedores,
} from "../../../api/proveedorApi";


const ESTADO_OPTIONS = [
  { value: "PENDIENTE", label: "PENDIENTE" },
  { value: "PAGADO", label: "PAGADO" },
];

const METODO_OPTIONS = [
  { value: "TRANSFERENCIA", label: "TRANSFERENCIA" },
  { value: "ACREDITACION_BANCARIA", label: "ACREDITACION_BANCARIA" },
  { value: "CHEQUE", label: "CHEQUE" },
];

const Proveedores = ({ metodo = "administrativo" }) => {
  const [proveedores, setProveedores] = useState([]);
  const [filteredProveedores, setFilteredProveedores] = useState([]);
  const [showProveedorModal, setShowProveedorModal] = useState(false);
  const [isEditingProveedor, setIsEditingProveedor] = useState(false);
  const [currentProveedor, setCurrentProveedor] = useState(null);

  const [listaProveedores, setListaProveedores] = useState([]);
  const [loadingStates, setLoadingStates] = useState({
    fetchingProveedores: false,
    addingProveedor: false,
    editingProveedor: false,
    deletingProveedor: false,
    loadingLista: false,
  });

  const [formData, setFormData] = useState({
    idProveedor: null,
    proveedor: "",
    ruc: "",
    razonsocial: "",
    factura: "",
    monto: "",
    fechaorden: "",
    estado: "PENDIENTE",
    comprobante: "",
    metododepago: "", // ⬅️ NUEVO
  });

  const [errors, setErrors] = useState({});

  const setLoading = (key, value) => {
    setLoadingStates((prev) => ({ ...prev, [key]: value }));
  };

  // 🔹 obtener pagos
  const fetchProveedores = useCallback(async () => {
    try {
      setLoading("fetchingProveedores", true);
      const response = await getAllPagoProveedores();
      const sinOrden = response.filter(
        (p) => !p.idordendepago || p.idordendepago === 0
      );
      setProveedores(response);
      setFilteredProveedores(sinOrden);
    } catch (err) {
      console.error("Error cargando proveedores:", err);
      setErrors({ proveedores: "Error al cargar proveedores." });
    } finally {
      setLoading("fetchingProveedores", false);
    }
  }, []);

  // 🔹 obtener lista base de proveedores
  const fetchListaProveedores = useCallback(async () => {
    try {
      setLoading("loadingLista", true);
      const lista = await getAllProveedores();
      const mapped = lista.map((p) => ({
        value: p.idProveedores,
        label: p.proveedor,
        ruc: p.ruc,
        razonsocial: p.razonsocial,
      }));
      setListaProveedores(mapped);
    } catch (err) {
      console.error("Error cargando lista proveedores:", err);
    } finally {
      setLoading("loadingLista", false);
    }
  }, []);

  useEffect(() => {
    fetchProveedores();
    fetchListaProveedores();
  }, [fetchProveedores, fetchListaProveedores]);

  const handleShowProveedorModal = (proveedor = null) => {
    if (proveedor) {
      setIsEditingProveedor(true);
      setCurrentProveedor(proveedor);
      setFormData({
        idProveedor: proveedor.idProveedor || null,
        proveedor: proveedor.proveedor || "",
        ruc: proveedor.ruc || "",
        razonsocial: proveedor.razonsocial || "",
        factura: proveedor.factura || "",
        monto: proveedor.monto || "",
        fechaorden: proveedor.fechaorden || "",
        estado: proveedor.estado || "PENDIENTE",
        comprobante: proveedor.comprobante || "",
        metododepago: proveedor.metododepago || "", // ⬅️ NUEVO
      });
    } else {
      setIsEditingProveedor(false);
      setCurrentProveedor(null);
      setFormData({
        idProveedor: null,
        proveedor: "",
        ruc: "",
        razonsocial: "",
        factura: "",
        monto: "",
        fechaorden: "",
        estado: "PENDIENTE",
        comprobante: "",
        metododepago: "", // ⬅️ NUEVO
      });
    }
    setErrors({});
    setShowProveedorModal(true);
  };

  const handleCloseProveedorModal = () => {
    setShowProveedorModal(false);
    setFormData({
      idProveedor: null,
      proveedor: "",
      ruc: "",
      razonsocial: "",
      factura: "",
      monto: "",
      fechaorden: "",
      estado: "PENDIENTE",
      comprobante: "",
      metododepago: "",
    });
    setErrors({});
    setIsEditingProveedor(false);
    setCurrentProveedor(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // 🔹 al seleccionar un proveedor
  const handleProveedorSelect = (selectedOption) => {
    if (selectedOption) {
      setFormData((prev) => ({
        ...prev,
        idProveedor: selectedOption.value,
        proveedor: selectedOption.label,
        ruc: selectedOption.ruc,
        razonsocial: selectedOption.razonsocial,
      }));
      setErrors((prev) => ({ ...prev, proveedor: "" }));
    } else {
      setFormData((prev) => ({
        ...prev,
        idProveedor: null,
        proveedor: "",
        ruc: "",
        razonsocial: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.idProveedor) newErrors.proveedor = "Seleccione un proveedor";
    if (!formData.factura.trim()) newErrors.factura = "La factura es requerida";
    if (!formData.monto || parseFloat(formData.monto) <= 0)
      newErrors.monto = "El monto debe ser mayor a 0";
    if (!formData.fechaorden) newErrors.fechaorden = "La fecha es requerida";

    // ⬇️ Validación condicionada: si está PAGADO, exigir comprobante y método
    if (String(formData.estado).toUpperCase() === "PAGADO") {
      if (!formData.comprobante || !String(formData.comprobante).trim()) {
        newErrors.comprobante = "Ingrese el comprobante para pagos en estado PAGADO";
      }
      if (!formData.metododepago || !String(formData.metododepago).trim()) {
        newErrors.metododepago = "Seleccione el método de pago para estado PAGADO";
      }
    }
    return newErrors;
  };

  const handleProveedorSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const key = isEditingProveedor ? "editingProveedor" : "addingProveedor";
    setLoading(key, true);

    try {
      const payload = {
        idpagoproveedor: isEditingProveedor ? currentProveedor.idpagoproveedor : null,
        idProveedor: formData.idProveedor,
        proveedor: formData.proveedor,
        ruc: formData.ruc,
        razonsocial: formData.razonsocial,
        factura: formData.factura,
        monto: formData.monto,
        fechaorden: formData.fechaorden,
        estado: formData.estado,
        comprobante: formData.comprobante || null,
        metododepago: formData.metododepago || null, // ⬅️ NUEVO
      };

      if (isEditingProveedor) {
        await editPagoProveedor(payload);
      } else {
        await addPagoProveedor(payload);
      }

      await fetchProveedores();
      handleCloseProveedorModal();

      Swal.fire({
        title: isEditingProveedor ? "Actualizado" : "Agregado",
        text: `El pago ha sido ${isEditingProveedor ? "actualizado" : "agregado"} con éxito.`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error guardando proveedor:", error);
      setErrors({
        submit: "Error al guardar el pago. Intente nuevamente.",
      });
    } finally {
      setLoading(key, false);
    }
  };

  const handleDeleteProveedor = async (idpagoproveedor) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "No podrás revertir esta acción.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      setLoading("deletingProveedor", true);
      try {
        await deletePagoProveedor(idpagoproveedor);
        await fetchProveedores();
        Swal.fire("Eliminado", "El pago ha sido eliminado.", "success");
      } catch (error) {
        console.error("Error eliminando pago:", error);
        setErrors({ proveedores: "Error al eliminar el registro." });
      } finally {
        setLoading("deletingProveedor", false);
      }
    }
  };

  const isPagado = String(formData.estado).toUpperCase() === "PAGADO";

  return (
    <div className="card">
      <div className="card-header border-0 pb-0">
        <div className="d-flex gap-2 align-items-center">
          <Button variant="primary" size="sm" onClick={() => handleShowProveedorModal()}>
            Agregar Pago a Proveedor
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={fetchProveedores}
            disabled={loadingStates.fetchingProveedores}
          >
            <i className="fas fa-sync-alt me-1"></i> Refrescar
          </Button>
        </div>
      </div>

      <div className="card-body">
        {errors.proveedores && <Alert variant="danger">{errors.proveedores}</Alert>}

        {loadingStates.fetchingProveedores ? (
          <div className="text-center">
            <Spinner animation="border" role="status" />
          </div>
        ) : filteredProveedores.length === 0 ? (
          <p>No hay pagos a proveedores registrados.</p>
        ) : (
          <PerfectScrollbar>
            <table className="table table-sm ">
              <thead style={{ backgroundColor: "#304a97", color: "white" }}>
                <tr>
                  <th>#ID</th>
                  <th>Proveedor</th>
                  <th>Factura</th>
                  <th>Monto</th>
                  <th>Fecha Orden</th>
                  <th>Estado</th>
                  <th>Método</th>       {/* ⬅️ NUEVO en grilla */}
                  <th>Comprobante</th>  {/* ⬅️ NUEVO en grilla */}
                  <th>Opciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredProveedores.map((prov) => (
                  <tr key={prov.idpagoproveedor}>
                    <td>{prov.idpagoproveedor}</td>
                    <td>{prov.proveedor}</td>
                    <td>{prov.factura}</td>
                    <td>{formatMiles(prov.monto)}</td>
                    <td>{prov.fechaorden}</td>
                    <td>{prov.estado}</td>
                    <td>{prov.metododepago || "-"}</td>   {/* ⬅️ NUEVO */}
                    <td>{prov.comprobante || "-"}</td>     {/* ⬅️ NUEVO */}
                    <td>
                      <div className="d-flex justify-content-center">
                        <button
                          className="btn btn-primary btn-sm me-1"
                          onClick={() => handleShowProveedorModal(prov)}
                        >
                          <i className="fas fa-pencil-alt"></i>
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteProveedor(prov.idpagoproveedor)}
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </PerfectScrollbar>
        )}
      </div>

      {/* MODAL */}
      <Modal show={showProveedorModal} onHide={handleCloseProveedorModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditingProveedor ? "Editar Pago a Proveedor" : "Agregar Pago a Proveedor"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errors.submit && <Alert variant="danger">{errors.submit}</Alert>}
          <Form onSubmit={handleProveedorSubmit}>
            {/* SELECT PROVEEDOR */}
            <Form.Group className="mb-3">
              <Form.Label>Proveedor <span className="text-danger">*</span></Form.Label>
              <Select
                options={listaProveedores}
                isLoading={loadingStates.loadingLista}
                placeholder="Seleccione un proveedor..."
                onChange={handleProveedorSelect}
                value={
                  listaProveedores.find(
                    (opt) => opt.value === formData.idProveedor
                  ) || null
                }
              />
              {errors.proveedor && (
                <Form.Text className="text-danger">{errors.proveedor}</Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>RUC</Form.Label>
              <Form.Control type="text" value={formData.ruc} readOnly />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Razón Social</Form.Label>
              <Form.Control type="text" value={formData.razonsocial} readOnly />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Factura *</Form.Label>
              <Form.Control
                type="text"
                name="factura"
                value={formData.factura}
                onChange={handleInputChange}
              />
              {errors.factura && <Form.Text className="text-danger">{errors.factura}</Form.Text>}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Monto *</Form.Label>
              <Form.Control
                type="number"
                name="monto"
                value={formData.monto}
                onChange={handleInputChange}
              />
              {errors.monto && <Form.Text className="text-danger">{errors.monto}</Form.Text>}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Fecha Orden *</Form.Label>
              <Form.Control
                type="date"
                name="fechaorden"
                value={formData.fechaorden}
                onChange={handleInputChange}
              />
              {errors.fechaorden && <Form.Text className="text-danger">{errors.fechaorden}</Form.Text>}
            </Form.Group>

            {/* Estado editable */}
            <Form.Group className="mb-3">
              <Form.Label>Estado</Form.Label>
              <Select
                options={ESTADO_OPTIONS}
                placeholder="Seleccione estado..."
                value={ESTADO_OPTIONS.find(o => o.value === formData.estado) || null}
                onChange={(opt) => {
                  setFormData(prev => ({ ...prev, estado: opt?.value || "PENDIENTE" }));
                  setErrors(prev => ({ ...prev, estado: "" }));
                }}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            </Form.Group>

            {/* Si es PAGADO, pedir Comprobante y Método */}
            {isPagado && (
              <>
                <Form.Group className="mb-2">
                  <Form.Label>
                    Comprobante <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="comprobante"
                    value={formData.comprobante}
                    onChange={handleInputChange}
                    placeholder="Ej: Nº transferencia / cheque / comprobante"
                  />
                  {errors.comprobante && (
                    <Form.Text className="text-danger">{errors.comprobante}</Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label>
                    Método de Pago <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    options={METODO_OPTIONS}
                    placeholder="Seleccione método..."
                    value={METODO_OPTIONS.find((o) => o.value === formData.metododepago) || null}
                    onChange={(opt) => {
                      setFormData((prev) => ({ ...prev, metododepago: opt?.value || "" }));
                      setErrors((prev) => ({ ...prev, metododepago: "" }));
                    }}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                  {errors.metododepago && (
                    <Form.Text className="text-danger">{errors.metododepago}</Form.Text>
                  )}
                </Form.Group>
              </>
            )}

          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleCloseProveedorModal}>
            Cerrar
          </Button>
          <Button variant="primary" onClick={handleProveedorSubmit}>
            {loadingStates.addingProveedor || loadingStates.editingProveedor ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                {isEditingProveedor ? "Actualizando..." : "Agregando..."}
              </>
            ) : isEditingProveedor ? (
              "Actualizar"
            ) : (
              "Agregar"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Proveedores;
